import { z } from 'zod';
import { addressSchema, documentSchema } from './core';
import { onlyDigits } from './documents';
import { subscriptionScheduleSchema } from './booking';

const money = z.coerce.number().int().min(0).max(100_000_000);
const zipCode = z.string().transform(onlyDigits).refine((v) => v.length === 8, 'CEP inválido.');

// ---------- Pedido ----------
export const ORDER_STATUSES = ['PENDING_PAYMENT', 'PAID', 'PREPARING', 'SHIPPED', 'DELIVERED', 'CANCELED', 'REFUNDED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const ORDER_CHANNELS = ['STOREFRONT', 'MANUAL', 'WHATSAPP'] as const;

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'Aguardando pagamento',
  PAID: 'Pago',
  PREPARING: 'Em separação',
  SHIPPED: 'Enviado',
  DELIVERED: 'Entregue',
  CANCELED: 'Cancelado',
  REFUNDED: 'Reembolsado',
};

/** Formas de pagamento oferecidas na loja. `MANUAL` = "combinar pagamento" com a loja. */
export const CHECKOUT_METHODS = ['PIX', 'CREDIT_CARD', 'BOLETO', 'MANUAL'] as const;
export type CheckoutMethod = (typeof CHECKOUT_METHODS)[number];
export const MAX_INSTALLMENTS = 12;

// ---------- Carrinho (público) ----------
export const setCartItemSchema = z.object({
  variantId: z.string().uuid(),
  /** 0 remove o item. */
  quantity: z.coerce.number().int().min(0).max(99),
});

export const applyCouponSchema = z.object({
  /** `null` remove o cupom. */
  code: z.string().trim().toUpperCase().min(2).max(40).nullable(),
});

export const shippingQuoteSchema = z.object({ zipCode });

export const buyerSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome completo.').max(120),
  document: documentSchema,
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  phone: z.string().trim().min(10, 'Telefone inválido.').max(25),
});

export const checkoutSchema = z
  .object({
    buyer: buyerSchema,
    /** Obrigatório quando a entrega não é retirada na loja. */
    address: addressSchema.omit({ isDefault: true }).optional(),
    shippingOptionId: z.string().min(1).max(60),
    payment: z.object({
      method: z.enum(CHECKOUT_METHODS),
      /** Token do cartão gerado no navegador pelo SDK do gateway (nunca o número). */
      cardToken: z.string().min(3).max(200).optional(),
      installments: z.coerce.number().int().min(1).max(MAX_INSTALLMENTS).default(1),
    }),
    notes: z.string().trim().max(500).optional(),
    marketingOptIn: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.payment.method === 'CREDIT_CARD' && !v.payment.cardToken) {
      ctx.addIssue({ code: 'custom', path: ['payment', 'cardToken'], message: 'Informe os dados do cartão.' });
    }
  });
export type CheckoutInput = z.infer<typeof checkoutSchema>;

// ---------- Painel ----------
export const COUPON_TYPES = ['PERCENT', 'FIXED', 'FREE_SHIPPING'] as const;

export const couponSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9_-]{2,40}$/, 'Use letras, números, hífen ou sublinhado.'),
    type: z.enum(COUPON_TYPES),
    /** Percentual (1–100) ou centavos. */
    value: z.coerce.number().int().min(0),
    minSubtotalCents: money.nullable().optional(),
    maxUses: z.coerce.number().int().min(1).nullable().optional(),
    startsAt: z.coerce.date().nullable().optional(),
    endsAt: z.coerce.date().nullable().optional(),
    active: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'PERCENT' && (v.value < 1 || v.value > 100)) ctx.addIssue({ code: 'custom', path: ['value'], message: 'Percentual entre 1 e 100.' });
    if (v.type === 'FIXED' && v.value < 1) ctx.addIssue({ code: 'custom', path: ['value'], message: 'Informe o valor do desconto.' });
  });
export const updateCouponSchema = z.object({
  active: z.boolean().optional(),
  maxUses: z.coerce.number().int().min(1).nullable().optional(),
  endsAt: z.coerce.date().nullable().optional(),
});

export const shipFromSchema = z.object({
  name: z.string().trim().min(2).max(120),
  document: documentSchema,
  phone: z.string().trim().min(10).max(25),
  email: z.string().trim().toLowerCase().email(),
  address: addressSchema.omit({ isDefault: true }),
});
export type ShipFrom = z.infer<typeof shipFromSchema>;

export const checkoutSettingsSchema = z
  .object({
    manualPaymentEnabled: z.boolean(),
    freeShippingAboveCents: money.nullable(),
    pickupEnabled: z.boolean(),
    shipFrom: shipFromSchema.nullable(),
  })
  .partial();

export const paymentAccountSchema = z.object({
  name: z.string().trim().min(3).max(120),
  email: z.string().trim().toLowerCase().email(),
  document: documentSchema,
  phone: z.string().trim().min(10).max(25),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  companyType: z.enum(['MEI', 'LIMITED', 'INDIVIDUAL', 'ASSOCIATION']).nullable().optional(),
  /** Faturamento (CNPJ) ou renda (CPF) mensal em centavos. Opcional: em branco, a API manda ao Asaas um valor aproximado. */
  incomeValueCents: z.coerce.number().int().min(1).max(100_000_000_00).nullable().optional(),
  address: addressSchema.omit({ isDefault: true, ibgeCode: true }),
});

export const listOrdersQuerySchema = z.object({
  status: z.enum(ORDER_STATUSES).optional(),
  channel: z.enum(ORDER_CHANNELS).optional(),
  customerId: z.string().uuid().optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  /** Número do pedido ou nome do cliente. */
  q: z.string().trim().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const shipOrderSchema = z.object({
  carrier: z.string().trim().min(2).max(60),
  trackingCode: z.string().trim().min(4).max(60).nullable().optional(),
});
export const cancelOrderSchema = z.object({ reason: z.string().trim().max(300).optional() });
export const refundOrderSchema = z
  .object({
    /** Ausente = reembolso total. */
    amountCents: z.coerce.number().int().min(1).optional(),
    /** Devolve os itens ao estoque (só no reembolso total). */
    returnToStock: z.boolean().default(true),
    reason: z.string().trim().max(300).optional(),
  });
export const markPaidSchema = z.object({
  method: z.enum(['PIX', 'CREDIT_CARD', 'BOLETO', 'CASH', 'OTHER']),
  feeCents: money.default(0),
});

// ---------- Cobrança presencial (painel) ----------
/**
 * Como o dono cobra no balcão: dinheiro e maquininha própria só registram o recebimento;
 * Pix e cartão (fatura do gateway aberta no celular do cliente) passam pela subconta.
 */
export const POS_CHARGE_METHODS = ['CASH', 'PIX', 'CARD_LINK', 'CARD_MACHINE', 'OTHER'] as const;
export type PosChargeMethod = (typeof POS_CHARGE_METHODS)[number];
export const POS_ONLINE_METHODS: readonly PosChargeMethod[] = ['PIX', 'CARD_LINK'];
export const POS_CHARGE_METHOD_LABELS: Record<PosChargeMethod, string> = {
  CASH: 'Dinheiro',
  PIX: 'Pix',
  CARD_LINK: 'Cartão no celular do cliente',
  CARD_MACHINE: 'Cartão na minha maquininha',
  OTHER: 'Outro',
};

export const posChargeSchema = z
  .object({
    /** Atendimento da agenda que está sendo cobrado: o serviço entra como item. */
    bookingId: z.string().uuid().optional().nullable(),
    items: z
      .array(
        z.object({
          variantId: z.string().uuid(),
          quantity: z.coerce.number().int().min(1).max(999),
          unitPriceCents: money.optional(),
        }),
      )
      .max(50)
      .default([]),
    customerId: z.string().uuid().optional().nullable(),
    customer: z
      .object({
        name: z.string().trim().min(2).max(120),
        email: z.string().trim().email().max(200).optional().nullable(),
        phone: z.string().trim().max(30).optional().nullable(),
      })
      .optional(),
    /** CPF ou CNPJ de quem paga; o gateway exige no Pix e no cartão. */
    document: documentSchema.optional().nullable(),
    method: z.enum(POS_CHARGE_METHODS),
    discountCents: money.default(0),
    tipCents: money.default(0),
    /** Taxa da maquininha própria (ou outro meio fora do gateway), para o financeiro. */
    feeCents: money.default(0),
    notes: z.string().trim().max(500).optional().nullable(),
  })
  .refine((v) => v.bookingId || v.items.length > 0, { message: 'Inclua ao menos um item.', path: ['items'] });
export type PosChargeInput = z.infer<typeof posChargeSchema>;

export const shippingCallbackSchema = z.object({ code: z.string().min(4).max(2000), state: z.string().min(8).max(2000) });

// ---------- Assinaturas da loja ----------
export const STORE_SUBSCRIPTION_STATUSES = ['PENDING', 'ACTIVE', 'PAST_DUE', 'CANCELED'] as const;
export type StoreSubscriptionStatus = (typeof STORE_SUBSCRIPTION_STATUSES)[number];

export const STORE_SUBSCRIPTION_STATUS_LABELS: Record<StoreSubscriptionStatus, string> = {
  PENDING: 'Aguardando o primeiro pagamento',
  ACTIVE: 'Ativa',
  PAST_DUE: 'Pagamento atrasado',
  CANCELED: 'Cancelada',
};

/**
 * Formas de pagamento da assinatura. No cartão o cliente paga o primeiro mês na página segura do
 * gateway e os seguintes são cobrados no mesmo cartão; Pix e boleto geram uma cobrança por mês.
 */
export const SUBSCRIPTION_METHODS = ['CREDIT_CARD', 'PIX', 'BOLETO'] as const;
export type SubscriptionMethod = (typeof SUBSCRIPTION_METHODS)[number];

/** Assinar um serviço na loja (público, com `Idempotency-Key`). */
export const storeSubscribeSchema = z.object({
  variantId: z.string().uuid(),
  buyer: buyerSchema,
  method: z.enum(SUBSCRIPTION_METHODS),
  marketingOptIn: z.boolean().optional(),
  /** Serviço com agenda: horário fixo ou marcar cada vez. */
  schedule: subscriptionScheduleSchema.optional(),
});
export type StoreSubscribeInput = z.infer<typeof storeSubscribeSchema>;

/** Área do cliente na loja: entrar com um código enviado por e-mail. */
export const storeLoginSchema = z.object({ email: z.string().trim().toLowerCase().email('E-mail inválido.') });
export const storeLoginVerifySchema = z.object({
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  code: z.string().trim().regex(/^\d{6}$/, 'O código tem 6 números.'),
});

export const listStoreSubscriptionsQuerySchema = z.object({
  status: z.enum(STORE_SUBSCRIPTION_STATUSES).optional(),
  /** Nome do cliente ou do serviço. */
  q: z.string().trim().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
